import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogFooter,
  ModalDialogHeader,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { AgreementRenderer } from '@/features/account/components/AgreementRenderer'
import { Checkbox } from '@/components/inputs/Checkbox'
import { FormField } from '@/components/Form'
import {
  SelectionLabel,
  SelectionLabelControl,
  SelectionLabelText,
} from '@/components/inputs/SelectionLabel'
import { type Ref, useCallback, useEffect } from 'react'
import { useScrollOffsets } from '@/hooks/element-hooks'
import { useCallbackRef } from '@/hooks/ref-hooks'
import { useMotionValueEvent } from 'motion/react'
import { animationFrameScheduler, of } from 'rxjs'
import { observeOn } from 'rxjs/operators'

export interface TermsOfUseAcceptanceDialogProps {
  isOpen: boolean
  isLoading: boolean
  termsMarkup: string
  agreed: boolean
  onAgreeChange: (agreed: boolean) => void
  onAccept: () => void
  onDecline: () => void
  isAcceptPending: boolean
  isScrolledToBottom: boolean
  scrollRef?: Ref<HTMLDivElement>
  testId?: string
  contentTestId?: string
  agreeCheckboxTestId?: string
  acceptButtonTestId?: string
  closeButtonTestId?: string
}

/**
 * Terms of Use acceptance dialog presentation component
 */
export function TermsOfUseAcceptanceDialog({
  isOpen,
  isLoading,
  termsMarkup,
  agreed,
  onAgreeChange,
  onAccept,
  onDecline,
  isAcceptPending,
  isScrolledToBottom,
  scrollRef,
  testId,
  contentTestId,
  agreeCheckboxTestId,
  acceptButtonTestId,
  closeButtonTestId,
}: TermsOfUseAcceptanceDialogProps) {
  return (
    <ModalDialog
      size="lg"
      isOpen={isOpen}
      onClose={onDecline}
      aria-label="Terms of Use"
      testId={testId}
    >
      <ModalDialogHeader />
      <ModalDialogContent
        ref={scrollRef}
        testId={!isLoading ? contentTestId : undefined}
      >
        <AgreementRenderer isSkeleton={isLoading} markup={termsMarkup} />
      </ModalDialogContent>
      <ModalDialogFooter>
        <div className="pb-2xl px-base">
          <FormField>
            <SelectionLabel>
              <SelectionLabelControl>
                <Checkbox
                  checked={agreed}
                  onChange={onAgreeChange}
                  disabled={!isScrolledToBottom}
                  testId={agreeCheckboxTestId}
                  isSkeleton={isLoading}
                />
              </SelectionLabelControl>
              <SelectionLabelText isSkeleton={isLoading}>
                {isScrolledToBottom
                  ? 'I have read and agree to the terms and conditions.'
                  : 'Please scroll to the bottom to read all terms before agreeing.'}
              </SelectionLabelText>
            </SelectionLabel>
          </FormField>
        </div>
        <ModalDialogActions>
          <Button
            label="Accept"
            onPress={onAccept}
            isDisabled={isLoading || !agreed}
            isPending={isAcceptPending}
            testId={acceptButtonTestId}
          />
          <Button
            label="Close"
            variant="outline"
            onPress={onDecline}
            isDisabled={isAcceptPending || isLoading}
            testId={closeButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface TermsScrollToBottomStateProps {
  /**
   * Current state for whether we've scrolled to the bottom
   */
  isScrolledToBottom: boolean
  /**
   * Called when we've scrolled to the bottom.
   * Make sure to `useCallback` this accordingly.
   */
  onScrolledToBottom: (value: true) => unknown
  /**
   * Whether the terms are currently loading.
   * We won't consider having scrolled to the bottom while loading to count.
   */
  isLoading?: boolean
}

/**
 * Hook for tracking whether a user has scrolled to the bottom of the
 * terms content.
 */
export function useTermsScrollToBottomState({
  isLoading,
  isScrolledToBottom,
  onScrolledToBottom,
}: TermsScrollToBottomStateProps) {
  const { scrollYEnd, connectRef } = useScrollOffsets()

  const checkScroll = useCallback(() => {
    if (isLoading) {
      return
    }
    const value = scrollYEnd.get()
    const isAtBottom = value <= 10
    if (isAtBottom && !isScrolledToBottom) {
      onScrolledToBottom(true)
    }
  }, [scrollYEnd, isLoading, isScrolledToBottom, onScrolledToBottom])

  useMotionValueEvent(scrollYEnd, 'change', checkScroll)

  useEffect(() => {
    // Ensures that if we transition from loading (new `checkScroll` instance due to `useCallback`)
    // that we'll wait with checking the scroll position until the browser has had time to
    // adjust the scroll port.
    const sub = of(0)
      .pipe(
        // Wait 3 frames
        observeOn(animationFrameScheduler),
        observeOn(animationFrameScheduler),
        observeOn(animationFrameScheduler),
      )
      .subscribe(checkScroll)
    return () => sub.unsubscribe()
  }, [checkScroll])

  return useCallbackRef(
    useCallback(
      (element: Element) => {
        const cleanup = connectRef(element)
        checkScroll()
        return cleanup
      },
      [checkScroll, connectRef],
    ),
  )
}
